import type { ProfileUserDataProtocolCardProps } from './PorfileUserDataProtocolCard.types';
import type { ReactElement } from 'react';

import { Card, DataList, Flex, Heading, Skeleton } from '@rango-dev/whale';
import BigNumber from 'bignumber.js';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { numberToString } from '../../../utils/Numbers';
import { Image } from '../../common/Image';

import styles from './PorfileUserDataProtocolCard.module.css';
import { CardFieldItem } from './ProfileUserDataCard';

const LOGO_SIZE = 40;
const SKELETON_LOGO_SIZE = '44px';
const SKELETON_TITLE_WIDTH = '110px';
const SKELETON_TITLE_HEIGHT = '28px';
const SKELETON_FIELD_COUNT = 4;

export function ProfileUserDataProtocolCard(props: ProfileUserDataProtocolCardProps): ReactElement {
  const { t } = useTranslation();

  if ('loading' in props) {
    return (
      <Card variant="surface" size="3">
        <Flex direction="column" align="center" gap="2">
          <Skeleton
            width={SKELETON_LOGO_SIZE}
            height={SKELETON_LOGO_SIZE}
            className={styles.logo}
          />
          <Skeleton width={SKELETON_TITLE_WIDTH} height={SKELETON_TITLE_HEIGHT} />
        </Flex>
        <DataList.Root size="2">
          {Array.from({ length: SKELETON_FIELD_COUNT }, (_, idx) => idx).map((idx) => (
            <CardFieldItem key={`field-skeleton-${idx}`} loading />
          ))}
        </DataList.Root>
      </Card>
    );
  }

  const { data, type } = props;
  const fields = [
    {
      id: 'txCount',
      label: type === 'dex' ? t('Swap Count') : t('Bridge Count'),
      value: data.txCount,
      description:
        type === 'bridge'
          ? t(
              "Bridge Count indicates the total count of bridge transactions you've completed using a specific bridge.",
            )
          : t(
              "Swap Count indicates the total count of swap transactions you've completed using a specific swap.",
            ),
    },
    {
      id: 'txCountEffective',
      label: type === 'dex' ? t('Effective Swap Count') : t('Effective Bridge Count'),
      value: data.txCountEffective,
      description:
        type === 'bridge'
          ? t(
              "Effective Bridge Count represents the total count of bridge transactions you've completed across the chains listed in your profile and can effect on your score.",
            )
          : t(
              "Effective Swap Count represents the total count of swap transactions you've completed in the chains listed in your profile and can effect on your score.",
            ),
    },
    {
      id: 'totalVolume',
      label: t('Total Volume'),
      value: '$' + numberToString(new BigNumber(data.volume)),
      description:
        type === 'bridge'
          ? t(
              'Total Volume represents the cumulative volume of your transactions using a specific bridge.',
            )
          : t(
              'Total Volume represents the cumulative volume of your transactions using a specific swap.',
            ),
    },
    {
      id: 'effectiveVolume',
      label: t('Effective Volume'),
      value: '$' + numberToString(new BigNumber(data.volumeEffective)),
      description:
        type === 'bridge'
          ? t(
              'Total Effective Volume represents the bridge transaction volume across the chains listed in your profile and can effect on your score.',
            )
          : t(
              'Total Effective Volume represents the swap transaction volume in the chains listed in your profile and can effect on your score.',
            ),
    },
  ];

  return (
    <Card variant="surface" size="3">
      <Flex direction="column" gap="5">
        <Flex direction="column" align="center" gap="2">
          <Image
            src={data.swapper.logo}
            width={LOGO_SIZE}
            height={LOGO_SIZE}
            className={styles.logo}
            alt={data.swapper.title}
          />
          <Heading size="3" weight="bold">
            {data.swapper.title}
          </Heading>
        </Flex>
        <DataList.Root size="2">
          {fields.map((field) => (
            <CardFieldItem
              key={field.id}
              label={field.label}
              value={field.value}
              description={field.description}
            />
          ))}
        </DataList.Root>
      </Flex>
    </Card>
  );
}
